import React, { useState, useMemo } from "react";
import {
  Table as TableElement,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
} from "@mhome/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import TimeGeneralCell from "./time-general-cell";
import NumberCell from "./number-cell";
import NumberUnitCell from "./number-unit-cell";
import FixedSelectCell from "./fixed-select-cell";

// Cell type mappings based on dataType
const getCellComponent = (dataType) => {
  switch (dataType) {
    case "Time":
      return TimeGeneralCell;
    case "Number":
      return NumberCell;
    case "Duration":
    case "Temperature":
    case "TemperatureDiff":
    case "Length":
    case "Weight":
      return NumberUnitCell;
    case "FixedSelect":
      return FixedSelectCell;
    default:
      return NumberCell; // Default fallback
  }
};

const Table = ({
  data = [],
  meta = {},
  columns = [],
  onChange,
  onAddRow,
  onDeleteRow,
  allowAdd = true,
  allowDelete = true,
  minRows = 1,
  bgcolor, // Background color for the table container
  ...tableProps
}) => {
  const [editingCell, setEditingCell] = useState(null); // { row, column }

  // If no columns provided, infer from meta
  const tableColumns =
    columns.length > 0
      ? columns
      : Object.keys(meta).map((key) => ({
          field: key,
          label: meta[key]?.label || key,
          dataType: meta[key]?.dataType || "Number",
        }));

  const handleCellEdit = (rowIndex, columnField) => {
    setEditingCell({ row: rowIndex, column: columnField });
  };

  const handleCellSave = () => {
    setEditingCell(null);
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCellChange = (rowIndex, columnField, newValue) => {
    const newData = [...data];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnField]: newValue,
    };
    onChange(newData);
  };

  const handleAddRow = () => {
    if (onAddRow) {
      onAddRow();
    } else {
      // Default add row behavior - create empty row with default values
      const newRow = {};
      tableColumns.forEach((col) => {
        const colMeta = meta[col.field] || {};
        switch (col.dataType) {
          case "Time":
            newRow[col.field] = "12:00:00";
            break;
          case "Duration":
            newRow[col.field] = "0min";
            break;
          case "Temperature":
            newRow[col.field] = "0°C";
            break;
          case "TemperatureDiff":
            newRow[col.field] = "0Δ°C";
            break;
          case "Length":
            newRow[col.field] = "0m";
            break;
          case "Weight":
            newRow[col.field] = "0kg";
            break;
          case "FixedSelect":
            // Use the first enum value or default value from meta
            const selectMeta = meta[col.field] || {};
            newRow[col.field] =
              selectMeta.defaultValue ||
              (selectMeta.enums && selectMeta.enums[0]) ||
              "";
            break;
          default:
            newRow[col.field] = "0";
        }
      });
      const newData = [...data, newRow];
      onChange(newData);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    if (onDeleteRow) {
      onDeleteRow(rowIndex);
    } else {
      // Default delete behavior
      if (data.length > minRows) {
        const newData = data.filter((_, index) => index !== rowIndex);
        onChange(newData);
      }
    }
  };

  const handleMoveRowUp = (rowIndex) => {
    if (rowIndex > 0) {
      const newData = [...data];
      [newData[rowIndex - 1], newData[rowIndex]] = [
        newData[rowIndex],
        newData[rowIndex - 1],
      ];
      onChange(newData);
    }
  };

  const handleMoveRowDown = (rowIndex) => {
    if (rowIndex < data.length - 1) {
      const newData = [...data];
      [newData[rowIndex], newData[rowIndex + 1]] = [
        newData[rowIndex + 1],
        newData[rowIndex],
      ];
      onChange(newData);
    }
  };

  const isEditing = (rowIndex, columnField) => {
    return (
      editingCell &&
      editingCell.row === rowIndex &&
      editingCell.column === columnField
    );
  };

  const renderCell = (row, rowIndex, column) => {
    const { field, dataType } = column;
    const value = row[field] || "";
    const colMeta = meta[field] || {};
    const CellComponent = getCellComponent(dataType);

    const cellProps = {
      value,
      onChange: (newValue) => handleCellChange(rowIndex, field, newValue),
      isEditing: isEditing(rowIndex, field),
      onEdit: () => handleCellEdit(rowIndex, field),
      onSave: handleCellSave,
      onCancel: handleCellCancel,
      meta: { ...colMeta, dataType },
    };

    return <CellComponent key={`${rowIndex}-${field}`} {...cellProps} />;
  };

  // Container style - horizontal scrolling only
  const containerStyle = useMemo(
    () => ({
      backgroundColor: bgcolor || "transparent",
    }),
    [bgcolor]
  );

  return (
    <TableContainer
      component="div"
      style={containerStyle}
      sx={{
        bgcolor: bgcolor || "transparent",
        overflowX: "auto",
        overflowY: "visible",
        "& table": {
          tableLayout: "fixed", // Fixed table layout for stable column widths
          borderRadius: "16px",
          overflow: "hidden",
        },
        "& thead tr:first-child th:first-child": {
          borderTopLeftRadius: "16px",
        },
        "& thead tr:first-child th:last-child": {
          borderTopRightRadius: "16px",
        },
        "& tbody tr:last-child td:first-child": {
          borderBottomLeftRadius: "16px",
        },
        "& tbody tr:last-child td:last-child": {
          borderBottomRightRadius: "16px",
        },
        "&::-webkit-scrollbar": {
          display: "block !important",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: document.documentElement.classList.contains("dark")
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.05)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: document.documentElement.classList.contains("dark")
            ? "rgba(255,255,255,0.3)"
            : "rgba(0,0,0,0.2)",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: document.documentElement.classList.contains("dark")
              ? "rgba(255,255,255,0.4)"
              : "rgba(0,0,0,0.3)",
          },
        },
        scrollbarWidth: "thin",
        scrollbarColor: document.documentElement.classList.contains("dark")
          ? "rgba(255,255,255,0.3) rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.2) rgba(0,0,0,0.05)",
        ...tableProps?.sx,
      }}
      {...tableProps}
    >
      <TableElement size="small">
        <TableHead>
          <TableRow>
            {tableColumns.map((column, index) => (
              <TableCell
                key={column.field}
                className="font-bold"
                style={{
                  width: index === 0 ? "200px" : "150px",
                  minWidth: index === 0 ? "200px" : "150px",
                  maxWidth: index === 0 ? "200px" : "150px",
                }}
              >
                {column.label}
              </TableCell>
            ))}
            {(allowDelete || allowAdd) && (
              <TableCell
                className="font-bold"
                style={{
                  width: "120px",
                  minWidth: "120px",
                  maxWidth: "120px",
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 && !allowAdd ? (
            <TableRow>
              <TableCell
                colSpan={
                  tableColumns.length + (allowDelete || allowAdd ? 1 : 0)
                }
                className="text-center py-8"
              >
                <Typography color="text.secondary">
                  No data available.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {data.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className="transition-colors bg-transparent"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(0, 0, 0, 0.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {tableColumns.map((column, columnIndex) => (
                    <TableCell
                      key={`${rowIndex}-${column.field}`}
                      className="overflow-hidden"
                      style={{
                        padding: "2px",
                        width: columnIndex === 0 ? "200px" : "150px",
                        minWidth: columnIndex === 0 ? "200px" : "150px",
                        maxWidth: columnIndex === 0 ? "200px" : "150px",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      {renderCell(row, rowIndex, column)}
                    </TableCell>
                  ))}

                  {(allowDelete || allowAdd) && (
                    <TableCell
                      style={{
                        padding: "2px",
                        width: "120px",
                        minWidth: "120px",
                        maxWidth: "120px",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                          WebkitTapHighlightColor: "transparent",
                        }}
                      >
                        {/* Move up button */}
                        <IconButton
                          size="small"
                          onClick={() => handleMoveRowUp(rowIndex)}
                          disabled={rowIndex === 0}
                          className="p-1"
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>

                        {/* Move down button */}
                        <IconButton
                          size="small"
                          onClick={() => handleMoveRowDown(rowIndex)}
                          disabled={rowIndex === data.length - 1}
                          className="p-1"
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>

                        {/* Delete button */}
                        {allowDelete && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRow(rowIndex)}
                            color="error"
                            disabled={data.length <= minRows}
                            className="p-1"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {/* Add new row inline */}
              {allowAdd && (
                <TableRow
                  className="transition-colors cursor-pointer"
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(0, 0, 0, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(0, 0, 0, 0.02)";
                  }}
                  onClick={handleAddRow}
                >
                  {tableColumns.map((column, columnIndex) => (
                    <TableCell
                      key={`add-${column.field}`}
                      className={`p-2 italic ${
                        columnIndex === 0 ? "text-left" : "text-center"
                      }`}
                      style={{
                        color: "hsl(var(--muted-foreground))",
                        width: columnIndex === 0 ? "200px" : "150px",
                        minWidth: columnIndex === 0 ? "200px" : "150px",
                        maxWidth: columnIndex === 0 ? "200px" : "150px",
                      }}
                    >
                      {columnIndex === 0 && (
                        <div className="flex items-center gap-2">
                          <AddIcon fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Add new row
                          </Typography>
                        </div>
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    className="p-2"
                    style={{
                      width: "120px",
                      minWidth: "120px",
                      maxWidth: "120px",
                    }}
                  />
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </TableElement>
    </TableContainer>
  );
};

export default Table;
